import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, ImagePlus } from "lucide-react";
import {
    listPortfolio,
    uploadPortfolioImage,
    updatePortfolioImage,
    deletePortfolioImage,
} from "../../lib/api";

// iter-144 — Superadmin "Website" tab: manage /portfolio images.

const CATEGORIES = [
    { key: "general", label: "General" },
    { key: "excavation", label: "Excavation" },
    { key: "septic", label: "Septic Services" },
    { key: "demolition", label: "Demolition" },
    { key: "site", label: "Site Services" },
];

export default function WebsiteTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadCat, setUploadCat] = useState("general");
    const [filter, setFilter] = useState("all");
    const fileInputRef = useRef(null);

    const load = async () => {
        try {
            setLoading(true);
            setItems(await listPortfolio());
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load portfolio.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const visible = useMemo(
        () => (filter === "all" ? items : items.filter((it) => it.category === filter)),
        [items, filter],
    );

    const handleFiles = async (files) => {
        if (!files || !files.length) return;
        setUploading(true);
        let ok = 0;
        for (const file of files) {
            try {
                await uploadPortfolioImage({ file, category: uploadCat });
                ok += 1;
            } catch (err) {
                toast.error(`${file.name}: ${err?.response?.data?.detail || "upload failed"}`);
            }
        }
        setUploading(false);
        if (ok > 0) {
            toast.success(`Uploaded ${ok} image${ok === 1 ? "" : "s"} to ${uploadCat}.`);
            load();
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleFiles(Array.from(e.dataTransfer?.files || []));
    };

    const handleUpdateCat = async (item, cat) => {
        try {
            await updatePortfolioImage(item.id, { category: cat });
            setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, category: cat } : x)));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm("Delete this image? This can't be undone.")) return;
        try {
            await deletePortfolioImage(item.id);
            setItems((prev) => prev.filter((x) => x.id !== item.id));
            toast.success("Deleted.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div className="space-y-5" data-testid="admin-website-tab">
            <section
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={handleDrop}
                className="rounded border-2 border-dashed border-[#2A3655] bg-[#0F1B3A]/40 p-6"
                data-testid="portfolio-uploader"
            >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#00A3FF] mb-1">
                            Website · Portfolio
                        </div>
                        <h3 className="text-lg font-bold text-white">
                            Upload jobsite photos to the public portfolio
                        </h3>
                        <p className="text-sm text-slate-400 mt-1">
                            Drop images here or click Choose files. They show up instantly
                            at <code className="text-[#00A3FF]">/portfolio</code>.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                            Category
                        </label>
                        <select
                            value={uploadCat}
                            onChange={(e) => setUploadCat(e.target.value)}
                            data-testid="portfolio-upload-category"
                            className="h-9 bg-[#0A1128] border border-[#2A3655] text-white text-sm rounded-sm px-2"
                        >
                            {CATEGORIES.map((c) => (
                                <option key={c.key} value={c.key}>{c.label}</option>
                            ))}
                        </select>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => handleFiles(Array.from(e.target.files || []))}
                            data-testid="portfolio-upload-input"
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            data-testid="portfolio-upload-btn"
                            className="inline-flex items-center gap-2 px-4 h-9 rounded-sm bg-[#007AFF] hover:bg-[#0369A1] text-white text-sm font-bold disabled:opacity-50"
                        >
                            {uploading ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                            ) : (
                                <><Upload className="w-4 h-4" /> Choose files</>
                            )}
                        </button>
                    </div>
                </div>
            </section>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-1">
                    <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>
                        All · {items.length}
                    </FilterChip>
                    {CATEGORIES.map((c) => {
                        const n = items.filter((x) => x.category === c.key).length;
                        return (
                            <FilterChip
                                key={c.key}
                                active={filter === c.key}
                                onClick={() => setFilter(c.key)}
                                testid={`portfolio-filter-${c.key}`}
                            >
                                {c.label} · {n}
                            </FilterChip>
                        );
                    })}
                </div>
                {loading && <span className="text-xs text-slate-500">Loading…</span>}
            </div>

            {visible.length === 0 && !loading ? (
                <div className="rounded border border-[#2A3655] bg-[#0F1B3A]/40 p-10 text-center text-slate-400" data-testid="portfolio-empty">
                    <ImagePlus className="w-8 h-8 mx-auto mb-2 text-slate-500" />
                    <div className="text-sm">No images yet. Upload above to get started.</div>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {visible.map((item) => (
                        <div
                            key={item.id}
                            className="rounded-sm overflow-hidden border border-[#2A3655] bg-[#0F1B3A]/60 group"
                            data-testid={`portfolio-item-${item.id}`}
                        >
                            <div className="relative aspect-[4/3] bg-slate-800">
                                <img
                                    src={item.url}
                                    alt={item.caption || item.category}
                                    loading="lazy"
                                    className="w-full h-full object-cover"
                                />
                                <button
                                    type="button"
                                    onClick={() => handleDelete(item)}
                                    data-testid={`portfolio-delete-${item.id}`}
                                    title="Delete"
                                    className="absolute top-1.5 right-1.5 w-7 h-7 rounded-sm bg-black/60 hover:bg-rose-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            <div className="p-2 flex items-center gap-2">
                                <select
                                    value={item.category}
                                    onChange={(e) => handleUpdateCat(item, e.target.value)}
                                    data-testid={`portfolio-cat-${item.id}`}
                                    className="flex-1 min-w-0 h-7 bg-[#0A1128] border border-[#2A3655] text-white text-xs rounded-sm px-1.5"
                                >
                                    {CATEGORIES.map((c) => (
                                        <option key={c.key} value={c.key}>{c.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function FilterChip({ active, onClick, testid, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm border transition-colors ${
                active
                    ? "bg-[#007AFF]/15 border-[#00A3FF] text-white"
                    : "border-[#2A3655] text-slate-300 hover:text-white hover:border-[#00A3FF]/60"
            }`}
        >
            {children}
        </button>
    );
}
